import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';
import { GraniteButton } from 'components/V2/Button/GraniteButton';
import { useCartContext } from 'screens/AccessExpressV3/CartProvider';
import { useModal } from 'hooks/useModal';
import ConfigRequirementsModal from './ConfigRequirementsModal/index';
import {
  CompanyPreferencesRequest,
  ConfigRequirements,
} from './ConfigRequirementsModal/schema';
import { DraftOrderAPIResponse } from 'api/accessexpressv3/schemas/OrdersSchemas';
import {
  getCompanyPreferences,
  postCompanyPreferences,
  postOrder,
  postQuote,
  updateOrder,
} from 'api/accessexpressv3/api';
import { useAuthUser } from 'hooks/useAuthUser';
import { postQuoteToRequest } from 'screens/AccessExpressV3/AccessProductDetails/utils';
import { useMutation, useQuery } from 'react-query';
import { useMemo } from 'react';
import {
  buildConfigRequirementsModalDefaults,
  buildItemPayload,
} from 'screens/AccessExpressV3/utils';
import Loader from 'components/Loader/Loader';
import clsx from 'clsx';
import { DIA_REFETCH_INTERVAL } from 'screens/AccessExpressV3/AccessProductDetails/AccessProductDetails';
import { ProductCategory } from 'screens/AccessExpressV3/accessExpress.types';

const LocationFooter = observer(() => {
  const cart = useCartContext();
  const navigate = useNavigate();
  const { open, dynamicProps, ...modalProps } = useModal();
  const userInfo = useAuthUser();

  const {
    data: companyPreferences,
    refetch: refetchCompanyPreferences,
    isFetching: isFetchingPreferences,
  } = useQuery(['company-preferences'], () => getCompanyPreferences(), {
    enabled: false,
  });

  const defaultValues = useMemo(
    () => buildConfigRequirementsModalDefaults(companyPreferences, cart),
    [companyPreferences, cart],
  );

  const { mutate: savePreferences } = useMutation(
    (data: CompanyPreferencesRequest) => postCompanyPreferences(data),
  );

  // const isAccessProductPage = location.pathname.includes('/access/product');

  const handleFindServices = async () => {
    cart.setLoadingDraftQuote(true);
    // 1. create a draft order with inserted locations
    let draft: DraftOrderAPIResponse | null = null;
    // NOTE: This logic is duplicated across multiple components (handleLocationsChange, syncCartWithBackend).
    const dataForPostOrUpdate = {
      additional_details:
        cart.selectedAccessProductCategory === 'All'
          ? {
              configs: cart.additionalDetails?.configs
                ? {
                    ...cart.additionalDetails?.configs,
                    ip_type: undefined,
                    ip_block: undefined,
                  }
                : {},
            }
          : cart.additionalDetails ?? {},
      locations: cart.locations.map((location) => {
        return {
          address: {
            ...location,
            id: location.id as string,
          },
          order_items: cart
            .itemsByLocation(location.id)
            .filter((item) => item.category === 'Access')
            .map(buildItemPayload),
        };
      }),
    };
    if (cart.draftId)
      draft = await updateOrder(dataForPostOrUpdate, cart.draftId);
    else
      draft = await postOrder({
        ...dataForPostOrUpdate,
        customer_name: userInfo.companyName,
        requester: `${userInfo.first_name} ${userInfo.last_name}`,
        status: 'draft',
      });

    // 2. await endpoint to return order locations
    // 3. store order locations on state;
    cart.updateStateFromDraft(draft);

    // if (!isAccessProductPage) {
    //   return;
    // }

    setTimeout(async () => {
      // 4. create quote
      if (draft?.id) {
        navigate(`access/product/${cart.selectedAccessProductCategory}`);

        if (cart.selectedAccessProductCategory === 'All') {
          return;
        }

        if (cart.selectedAccessProductCategory === 'DIA') {
          cart.setDIAFlowActive(true);
        }
        await postQuote(
          postQuoteToRequest(userInfo, cart.locations, [
            {
              product_type:
                cart.selectedAccessProductCategory as ProductCategory,
              ...cart.additionalDetails?.configs,
            },
          ]),
        )
          .then(async (quoteResponse) => {
            const additionalDetails = {
              configs: cart.additionalDetails?.configs,
              ...(cart.selectedAccessProductCategory === 'Starlink'
                ? { starlink_quote_request: quoteResponse.id }
                : cart.selectedAccessProductCategory === 'DIA'
                  ? { dia_quote_request: quoteResponse.id }
                  : { bb_quote_request: quoteResponse.id }),
            };
            await updateOrder(
              {
                additional_details: additionalDetails,
              },
              draft?.id ?? '',
            )
              .then((data) => cart.updateStateFromDraft(data))
              .finally(() => {
                if (cart.selectedAccessProductCategory === 'DIA') {
                  if (quoteResponse.status === 'Checking availability') {
                    setTimeout(() => {
                      cart.setStartPolling(true);
                    }, DIA_REFETCH_INTERVAL);
                  } else if (quoteResponse.status === 'Closed') {
                    cart.setLoadingDraftQuote(false);
                    cart.setStartPolling(false);
                  }
                } else {
                  cart.setLoadingDraftQuote(false);
                }
                cart.setCartState('default');
              });
          })
          .catch((error) => {
            cart.setLoadingDraftQuote(false);
            console.log('Error creating quote:', error);
          });
      }
      // 5. the request will return quote_id
      // 6. update draft (BE + state) with quote_id
    }, 0);
  };

  const onConfirmation = (data: ConfigRequirements) => {
    if (data.save_default) {
      savePreferences({
        ...data,
        term: data.term.value,
        exclude_carriers: data.exclude_carriers?.map((item) => item.value),
        preferred_carriers: data.preferred_carriers?.map((item) => item.value),
        include_carriers: data.preferred_carriers?.map((item) => item.value),
        ip_type: data.ip_type?.value,
        ip_block: data.ip_block?.value,
        min_download: data.download.value,
        max_download: data.download.value,
      });
    }
    cart.setConfigs(data);
    handleFindServices();
  };

  const onFindServicesClick = async () => {
    if (cart.selectedAccessProductCategory === 'Starlink') {
      handleFindServices();
      return;
    }
    await refetchCompanyPreferences();
    open();
  };

  return (
    <div className="flex flex-col gap-8">
      <div className="flex w-full flex-row gap-4">
        <GraniteButton
          variant="secondary"
          onClick={() => cart.close()}
          className="w-full"
        >
          Back
        </GraniteButton>
        <GraniteButton
          variant="tertiary"
          onClick={onFindServicesClick}
          className={clsx('w-full', isFetchingPreferences && 'loading-button')}
          disabled={
            !cart.selectedAccessProductCategory ||
            cart.locations.length === 0 ||
            cart.loadingDraftQuote ||
            isFetchingPreferences
          }
        >
          Find services
          {isFetchingPreferences && (
            <Loader animationClassname="!w-3 !h-3 border-t-button-background-tertiary-default" />
          )}
        </GraniteButton>
      </div>
      <ConfigRequirementsModal
        {...modalProps}
        isLoading={cart.loadingDraftQuote}
        onConfirmation={onConfirmation}
        defaultValues={defaultValues}
      />
    </div>
  );
});

export default LocationFooter;
